/*
 * Proprietary and Confidential
 *
 * Copyright (c) [2018] -  [] Marcelo H. Krebber - Munich, London 2018
 * All Rights Reserved.
 *
 * Dissemination or reproduction of this file [configuration.js] or parts within
 * via any medium is strictly forbidden unless prior written permission is obtained
 * from <marcelo.krebber@gmail.com>
 *
 * Last modified: 25.01.19 17:22
 */

import React, {useContext, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import moment from "moment";
import 'moment/locale/de';
import 'moment/locale/en-gb';
import 'moment/locale/es';
import {
    addDays, closestIndexTo,
    compareDesc,
    format,
    formatDistanceToNow,
    isAfter, isBefore, isEqual,
    isFuture,
    isPast, isSameDay, isSameMonth,
    isThisMonth, isWithinInterval,
    lastDayOfMonth, parseISO, subDays,
    subMonths
} from "date-fns";
import Waves from "node-waves";

import {
    getLocalSiteConfiguration,
    saveLocaleToLocalStorage,
    saveLocalSiteConfiguration
} from "../../actions/local-storage";
import {asyncFetchConfiguration} from "../../actions";
import {getCopyScriptName, getDefaultTheme, getLanguageScriptName, getLocale} from '../../actions/environment';
import {registerLocale} from "react-datepicker";
import {isMobileMozilla} from "./mobile";
import {NoInternetConnection, ServerNotAlive} from "../not-found/no-internet";
import {importDateFnsLocale} from "./locale";
import {isOnline, isServerAlive} from "../../actions/error-handling";

export const ConfigurationContext = React.createContext({});
export let localeFns = null;

// detects if the app is initially started on a mobile device
window._isMobile_ = isMobileMozilla();
window._isSidebarHidden_ = false;

// initialize waves ui-effects engine
Waves.init();

const DEFAULT_LANGUAGE_PATH = '/static/copy';

// const DEFAULT_LANGUAGE_PATH = '/Users/amaru/Kikirikii/holdfast/public-upload/locale';

export function loadLanguageScript(filename, callback) {
    const scriptPath = `${DEFAULT_LANGUAGE_PATH}/${filename}`;

    try {
        let elemScript = document.createElement("script");
        elemScript.setAttribute("src", scriptPath);
        document.body.appendChild(elemScript);

        elemScript.addEventListener("load", scriptLoaded, false);

        function scriptLoaded() {
            callback && callback(true);
        }
    } catch (error) {
        callback && callback(false);
    }
}

const Configuration = ({children}) => {
    const [theme, setTheme] = useState(getDefaultTheme());
    const [locale, setLocale] = useState(getLocale());
    const [Lang, setLanguage] = useState(window._language_);
    const [Copy, setCopy] = useState(window._copy_);
    const configuration = useSelector(state => state.configuration);
    const dispatch = useDispatch();

    useEffect(() => {
        loadLanguageScript(getLanguageScriptName(locale), (loaded) => {
            if (loaded) {
                saveLocaleToLocalStorage(locale);
                setLanguage(window._language_);
            }
        });
    }, [locale])

    useEffect(() => {
        loadLanguageScript(getCopyScriptName(locale), (loaded) => {
            loaded && setCopy(window._copy_);
        });
    }, [locale])

    useEffect(() => {
        // moment locale - refactor to use date-fns only
        // const localeMoment = importMomentLocale(Lang.locale);
        // moment.locale(localeMoment[Lang.locale]);
        moment.locale(Lang.locale);

        // date picker locale - await not working here
        importDateFnsLocale(Lang.locale).then((result) => {
            registerLocale(Lang.locale, result.default);
            localeFns = result.default;
        })

        dispatch(asyncFetchConfiguration(configuration => {
            isServerAlive(true);
            saveLocalSiteConfiguration(configuration);
        }));
    }, [Lang, window._isServerAlive_]);

    if (!isOnline()) return <NoInternetConnection/>
    if (!isServerAlive()) return <ServerNotAlive/>

    const config = configuration || getLocalSiteConfiguration();
    if (!config) return null;

    return <ConfigurationContext.Provider value={{
        Copy: Copy,
        Lang: Lang,
        theme: theme,
        locale: locale,
        setLanguage: setLanguage,
        setCopy: setCopy,
        setTheme: setTheme,
        setLocale: setLocale
    }}>
        <div className={theme}>
            {children}
        </div>
    </ConfigurationContext.Provider>
}

export default Configuration;

// wrapper for format-fns with locale
export const formatFns = (date, pattern) => {
    return format(date, pattern, {locale: localeFns})
}

export const formatFnsToNow = date => {
    return formatDistanceToNow(new Date(date), {addSuffix: true, locale: localeFns})
}

export const todayLongFns = (date, pattern) => {
    return format(new Date(), 'MMM dd yyyy, EEEE', {locale: localeFns})
}

export const formatLongFns = (date, pattern = 'MMM dd yyyy, EEEE') => {
    return format(new Date(date), pattern, {locale: localeFns})
}

export const formatShortFns = (date, pattern = 'MMM dd yyyy, EE') => {
    return format(new Date(date), pattern, {locale: localeFns})
}

export const closestIndexToNowFns = (dates) => {
    return closestIndexTo(new Date(), dates);
}

// Compare the two dates and return -1 if the first date is after the second,
// 1 if the first date is before the second or 0 if dates are equal.
export const compareDescFns = (left, right) => {
    return compareDesc(left, right);
}

export const isPastFns = (date, adjustDays = 0) => {
    let past = new Date(date);
    if (adjustDays) {
        past = addDays(past, adjustDays);
    }
    return isPast(past)
}

export const isFutureFns = (date) => {
    return isFuture(new Date(date))
}

export const isThisMonthFns = (date) => {
    return isThisMonth(new Date(date))
}

// isodate as yyyy-mm-dd
export const isSameDayFns = (isoDate, right) => {
    const left = new Date(isoDate);
    return isSameDay(left, right);
}

export const isSameMonthFns = (isoDate, right) => {
    const left = new Date(isoDate);
    return isSameMonth(left, right);
}

export const calendarDateFns = (date) => {
    return format(new Date(date), 'dd.MM.yyyy', {locale: localeFns})
}

export const dayOfWeekFns = (isoDate) => {
    const date = new Date(isoDate);
    return format(date, 'eeee', {locale: localeFns});
}

// essentially get rid of day portion
export const monthAndYearNameFns = (isoDate) => {
    const date = new Date(isoDate);
    return format(date, 'MMMM yyyy', {locale: localeFns});
}

export const firstDayOfMonthFns = (isodate) => {
    const date = new Date(isodate);
    return format(date, 'yyyy-MM-01', {locale: localeFns});
}

export const subtractMonths = (date, months) => {
    return subMonths(date, months);
}

export const subtractDays = (date, days) => {
    return subDays(date, days);
}

export const asIsoDate = (date) => {
    return format(date, 'yyyy-MM-dd', {locale: localeFns});
}

// get end date(last day) of past month - dates-fns based
export const getLastDayOfPastMonth = () => {
    const boundaryDate = subMonths(new Date(), 1);
    return lastDayOfMonth(boundaryDate);
}

export function getLocaleFns() {
    return localeFns;
}

// check if first date is after second date (boundary) - dates-fns based
export const isDateAfter = (date, boundary) => {
    return isAfter(date, boundary);
}

// date as Date, start and end as string in ISO format YYYY-MM-DD
export const isDateWithinInterval = (date, isoStart, isoEnd) => {
    if (isoStart === null) {
        return false;

    } else if (isoEnd === null) {
        return isSameDayFns(parseISO(isoStart), new Date());

    } else if (isAfter(parseISO(isoEnd), parseISO(isoStart))) {
        return isWithinInterval(date, {start: parseISO(isoStart), end: parseISO(isoEnd)});
    }
    return false;
}

// similar to isDateWithinInterval() but for calendar dates - date as Date,
// start and end as json calendar dates (fields: date, startTime, endTime)
export const isCalendarDateWithinInterval = (date, start, end) => {
    if (start === null) {
        return false;

    } else if (end === null) {
        // return isSameDayFns(parseISO(start.date), new Date());
        return isSameDayFns(parseISO(start.date), date);

    } else if (isAfter(parseISO(end.date), parseISO(start.date))) {
        return isWithinInterval(date,
            {
                start: parseISO(start.date),
                end: addDays(parseISO(end.date),1 ) // hack for date equals end-date
            });
    }
    return false;
}

// check if first date is after second date (boundary) - dates-fns based
// date as string in ISO format YYYY-MM-DD
export const isISODateAfter = (isoDate, boundary) => {
    const date = new Date(isoDate);
    return isAfter(date, boundary);
}

export const useConfiguration = () => {
    const {Lang, Copy} = useContext(ConfigurationContext);
    return {Copy: Copy, Lang: Lang, isMobile: window._isMobile_, isServerAlive: isServerAlive()};
}

export const useLanguage = () => {
    const {Lang} = useContext(ConfigurationContext);
    return Lang;
}

export const useIsMobile = (force = false) => {
    if (force) {
        window._isMobile_ = isMobileMozilla();
    }
    return window._isMobile_;
}

export const useLocalSiteConfiguration = () => {
    // return getLocalConfiguration();
    const siteConf = getLocalSiteConfiguration();
    return siteConf;
}

export const isSideBarHidden = (hidden) => {
    if (hidden !== undefined) {
        window._isSidebarHidden_ = hidden;
    }
    return window._isSidebarHidden_;
}

